// ContactLinkDetailModal.js - Complete Fixed Version
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Box, Typography, IconButton, TextField, Button, Slider, CircularProgress, Modal } from '@mui/material';
import TeamModal from 'team/shared/components/TeamModal';
import { CloseIcon } from 'icons/materialIcons';
import { getContactLinksByCard } from 'apis/contactLinkApi';
import { ContactLinkBaseUrlContext } from 'contexts/ContactLinkBaseUrlContext';
import TeamSample from './TeamSample';
import TeamMemberDigitalCardPreview from 'team/components/TeamMemberDigitalCardPreview';
import { formatContactLink } from 'utils/formatContactLink';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import getIconForType from 'utils/icons/getIconForType';
import { useAuth } from 'contexts/AuthContext';
import { useUserContext } from 'contexts/UserContext';
import { useCardContext } from 'contexts/CardContext';
import { useParams, useNavigate } from 'react-router-dom';
import { createContactLink } from 'apis/contactLinkApi';
import loadGoogleMapsScript from 'utils/loadGoogleMapsScript';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'team/shared/utils/images/teamCropImageUtils';
import { validateUUID } from 'utils/validator';
import TeamProductsServicesForm from './productsServices/TeamProductsServicesForm';
import { uploadImageBlob } from 'apis/imageStorageApi'; // ✅ ADD: Import uploadImageBlob
import TeamPhoneInputField from 'team/shared/components/TeamPhoneInputField';
import { isPhoneContactType } from 'utils/phoneUtils';
import { API_BASE_URL } from 'utils/apiUtils';
import { getContactLinkCategory } from 'utils/getContactLinkCategory';
import * as Sentry from '@sentry/react';
import { getCardById, updateCard } from 'apis/cardApi'; // ✅ ADD: Import getCardById and updateCard
import { getUserById } from 'apis/authApi'; // ✅ ADD: Import getUserById to get user phone number
import { useTeam } from 'team/contexts/teamContext';
import { useTeamAssets } from 'team/hooks/useTeamAssets';
import { TEAM_ASSET_TYPES } from 'team/apis/teamAssetApi';
import TeamAssetPickerPopover from 'team/components/editMember/about/profilePhotoCoverPhotoCompanyLogo/TeamAssetPickerPopover';

const TeamContactLinkDetailModal = ({
    open,
    handleClose,
    userId,
    cardId,
    contactLinkType,
    existingContactLinks,
    isTemporaryCard = false,
    isInSignupFlow = null
}) => {

    // State for contact link details
    const [linkTitle, setLinkTitle] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [linkType, setLinkType] = useState('');
    const [isHighlighted, setIsHighlighted] = useState(false);
    const { baseUrls } = useContext(ContactLinkBaseUrlContext);
    const {
        contactLinks,
        setContactLinksForCard,
        fetchContactLinksForCard,
        addContactLink,
        setCurrentCard,
        isInSignupFlow: contextIsInSignupFlow,
        forceRefetchCard,  // ✅ ADD FORCE REFETCH
        currentCardId      // ✅ ADD CURRENT CARD ID
    } = useContactLinkContext();

    // ✅ ADD: Use CardContext to access card data and loading functions
    const {
        cardState,
        loadCardFromServer,
        isTemporaryCard: isTemporaryCardFromContext,
        updateCardData // ✅ ADD updateCardData
    } = useCardContext();

    const [placeholder, setPlaceholder] = useState('');
    const [label, setLabel] = useState('');
    const [baseUrl, setBaseUrl] = useState('');

    const [contactLink, setContactLink] = useState(null);
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'warning'
    });

    const [workAddress, setWorkAddress] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [unit, setUnit] = useState('');
    const [predictions, setPredictions] = useState([]);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [coordinates, setCoordinates] = useState(null);

    const [iconImage, setIconImage] = useState(null);
    const [croppedIcon, setCroppedIcon] = useState(null);
    const [isIconCropperOpen, setIsIconCropperOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [iconPickerAnchorEl, setIconPickerAnchorEl] = useState(null);

    const [productsServicesData, setProductsServicesData] = useState({});
    const [phoneError, setPhoneError] = useState('');
    const [isLinkBeingCreated, setIsLinkBeingCreated] = useState(false); // Restored state
    const [showUrlRequired, setShowUrlRequired] = useState(false);
    const [croppedIconBlob, setCroppedIconBlob] = useState(null); // ✅ ADD: State for icon blob
    const [uploadingIcon, setUploadingIcon] = useState(false); // ✅ ADD: State for icon upload loading

    const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
        const containerWidth = window.innerWidth * 0.9;
        const containerHeight = window.innerHeight * 0.9;
        const widthRatio = containerWidth / mediaWidth;
        const heightRatio = containerHeight / mediaHeight;
        return Math.min(widthRatio, heightRatio);
    }, []);

    const onMediaLoaded = useCallback((mediaSize) => {
        const initialZoom = calculateInitialZoom(mediaSize.width, mediaSize.height);
        setZoom(initialZoom);
        setCrop({ x: 0, y: 0 });
    }, [calculateInitialZoom]);
    const iconInputRef = useRef(null);
    const { currentTeamId } = useTeam();
    const { assets: linkIconAssets = [] } = useTeamAssets(currentTeamId, TEAM_ASSET_TYPES.LINK_ICON);
    const navigate = useNavigate();

    // Get auth context for debugging
    const { authState } = useAuth();

    // ✅ CONTEXT-ONLY: Enhanced flow detection
    const determineFlowType = () => {
        // ✅ FIXED: Properly handle isInSignupFlow - it's a function, not a boolean
        const isSignupFlowActive = typeof isInSignupFlow === 'function' ? isInSignupFlow() : (isInSignupFlow || false);
        const hasUser = !!userId;
        const isTempCard = cardId === 'tempCardId' || cardId?.startsWith('temp_') || isTemporaryCard;

        // ✅ FIX: Detect UUID format for new card creation
        const isUuidCardCreation = cardId && cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

        // ✅ FIX: Check card creation context flags
        const isInCardCreationContext = localStorage.getItem('cardCreationInProgress') === 'true' ||
            sessionStorage.getItem('newCardCreation') === 'true';

        // ✅ FIXED: Check if this is a permanent MongoDB ObjectId (24 characters, hex, no hyphens)
        const isPermanentCard = cardId && cardId.length === 24 && /^[0-9a-fA-F]{24}$/.test(cardId);

        const debugInfo = {
            cardId: cardId?.substring(0, 8) + '...',
            isSignupFlowActive,
            isTempCard,
            isUuidCardCreation, // ✅ NEW
            isInCardCreationContext, // ✅ NEW
            isPermanentCard,
            hasUser
        };

        // Console log for development
        console.log('🔍 FLOW_DETECTION:', debugInfo);

        // Send to Sentry only in production
        if (process.env.NODE_ENV === 'production') {
            Sentry.addBreadcrumb({
                category: 'contact-link-detail-modal',
                message: 'Flow Detection',
                data: debugInfo,
                level: 'info'
            });
        }

        // ✅ DECISION: Use CONTEXT_ONLY for ALL creation scenarios
        if (isSignupFlowActive ||
            isTempCard ||
            isUuidCardCreation ||          // ✅ NEW: UUID cards use CONTEXT_ONLY
            isInCardCreationContext) {     // ✅ NEW: Card creation context
            return 'CONTEXT_ONLY';
        }
        // ✅ DATABASE_SYNC: Only for truly established cards
        else if (isPermanentCard && hasUser && !isInCardCreationContext) {
            return 'DATABASE_SYNC';
        }
        // ✅ DEFAULT: CONTEXT_ONLY for safety
        else {
            return 'CONTEXT_ONLY';
        }
    };

    const currentFlowType = determineFlowType();

    // 🔍 COMPREHENSIVE DEBUG FUNCTION
    const debugUserIdIssue = () => {
        const debugInfo = {
            userId,
            cardId,
            isTemporaryCard,
            authState_exists: !!authState,
            authState_user_exists: !!authState?.user,
            authState_userId: authState?.user?.userId,
            flowType: currentFlowType
        };

        // Console log for development
        console.log('🔍 =================== USER_ID_DEBUG_START ===================');
        console.log('🔍 USER_ID_DEBUG:', debugInfo);
        console.log('🔍 =================== USER_ID_DEBUG_END ===================');

        // Send to Sentry only in production
        if (process.env.NODE_ENV === 'production') {
            Sentry.addBreadcrumb({
                category: 'contact-link-detail-modal',
                message: 'User ID Debug Info',
                data: debugInfo,
                level: 'info'
            });
        }
    };

    // Debug on modal open and state changes
    useEffect(() => {
        if (open) {
            debugUserIdIssue();
        }
    }, [open, userId, cardId, currentFlowType]);

    // 🔍 ENHANCED DEBUG FOR CONTACT LINK CREATION
    const debugContactLinkCreation = (contactLinkData) => {
        const debugInfo = {
            contactLinkData,
            userId,
            cardId,
            contactLinkType,
            isTemporaryCard,
            currentFlowType
        };

        // Console log for development
        console.log('🔍 =================== CONTACT_LINK_CREATION_DEBUG ===================');
        console.log('🔍 CONTACT_LINK_CREATION_DEBUG:', debugInfo);
        console.log('🔍 =================== CONTACT_LINK_CREATION_DEBUG_END ===================');

        // Send to Sentry only in production
        if (process.env.NODE_ENV === 'production') {
            Sentry.addBreadcrumb({
                category: 'contact-link-detail-modal',
                message: 'Contact Link Creation Debug',
                data: debugInfo,
                level: 'info'
            });
        }
    };

    const handleIconUpload = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            setIconImage(URL.createObjectURL(file));
            setIsIconCropperOpen(true);
            setIconPickerAnchorEl(null);
        }
    };

    const onIconCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleIconCropSave = async () => {
        try {
            const croppedImage = await getCroppedImg(
                iconImage,
                croppedAreaPixels,
                'rectangle'
            );
            const previewUrl = URL.createObjectURL(croppedImage);
            setCroppedIcon(previewUrl);
            setCroppedIconBlob(croppedImage); // ✅ Store the blob for uploading later
            setIsIconCropperOpen(false);

            if (iconImage) {
                URL.revokeObjectURL(iconImage);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        return () => {
            if (iconImage) {
                URL.revokeObjectURL(iconImage);
            }
        };
    }, [iconImage]);

    const handleCloseSuccessSnackbar = () => {
        setSuccessSnackbarOpen(false);
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // ✅ ENHANCED: Modal close with force refetch
    const handleModalClose = () => {
        setLinkTitle('');
        setLinkUrl('');
        setCroppedIcon(null);
        setIconImage(null);
        setWorkAddress('');
        setZipCode('');
        setUnit('');
        setPredictions([]);
        setSelectedPlace(null);
        setCoordinates(null);
        setContactLink(null);
        setProductsServicesData({});
        setIsLinkBeingCreated(false);
        setCroppedIconBlob(null); // ✅ Reset blob state
        setUploadingIcon(false); // ✅ Reset uploading state
        setIconPickerAnchorEl(null);

        // ✅ CRITICAL FIX: Force refetch card data when closing modal
        // Only for non-temporary cards and when we have a valid cardId
        const targetCardId = currentCardId || cardId;
        const isSignupFlowActive = typeof isInSignupFlow === 'function' ? isInSignupFlow() : false;

        if (targetCardId && !isTemporaryCard && !isSignupFlowActive && currentFlowType === 'DATABASE_SYNC') {
            console.log('🔍 ContactLinkDetailModal: Force refetching card after modal close:', targetCardId);

            // Use setTimeout to ensure any pending operations complete
            setTimeout(() => {
                forceRefetchCard(targetCardId);
            }, 1000); // 1 second delay to ensure database sync
        } else {
            console.log('🔍 ContactLinkDetailModal: Skipping force refetch - temporary card or signup flow');
        }

        handleClose();
    };

    const handleIconPickerOpen = (event) => {
        setIconPickerAnchorEl(event.currentTarget);
    };

    const handleIconPickerClose = () => {
        setIconPickerAnchorEl(null);
    };

    const handleUploadOwnIcon = () => {
        if (iconInputRef.current) {
            iconInputRef.current.click();
        }
    };

    const handleSelectTeamAssetIcon = (asset) => {
        if (!asset?.url) return;
        setCroppedIcon(asset.url);
        setIconPickerAnchorEl(null);
    };

    useEffect(() => {
        if (open && contactLinkType === 'Address') {
            if (window.google) {
                initializeAutocomplete();
            } else {
                loadGoogleMapsScript()
                    .then(() => {
                        initializeAutocomplete();
                    })
                    .catch(error => {
                        console.error('Failed to load Google Maps:', error);
                    });
            }
        }
    }, [open, contactLinkType]);

    const initializeAutocomplete = () => {
        const tryInitialize = () => {
            const input = document.getElementById('work-address-input');
            if (input && window.google) {
                if (input.dataset.autocompleteHandler) {
                    input.removeEventListener('input', input.dataset.autocompleteHandler);
                }

                const autocomplete = new window.google.maps.places.AutocompleteService();

                const handleInput = (e) => {
                    if (e.target.value) {
                        autocomplete.getPlacePredictions(
                            { input: e.target.value },
                            (predictions, status) => {
                                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                                    setPredictions(predictions);
                                }
                            }
                        );
                    } else {
                        setPredictions([]);
                    }
                };

                input.addEventListener('input', handleInput);
                input.dataset.autocompleteHandler = handleInput;
                return true;
            }
            return false;
        };

        if (!tryInitialize()) {
            setTimeout(() => {
                if (!tryInitialize()) {
                    setTimeout(tryInitialize, 500);
                }
            }, 100);
        }
    };

    const geocodeAddress = async (address) => {
        return new Promise((resolve, reject) => {
            if (!window.google) {
                reject(new Error('Google Maps not loaded'));
                return;
            }

            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const location = results[0].geometry.location;
                    const coords = {
                        lat: location.lat(),
                        lng: location.lng()
                    };
                    resolve(coords);
                } else {
                    reject(new Error('Geocoding failed'));
                }
            });
        });
    };

    const handlePlaceSelect = (prediction) => {
        const placesService = new window.google.maps.places.PlacesService(
            document.createElement('div')
        );

        placesService.getDetails(
            { placeId: prediction.place_id },
            (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    setWorkAddress(place.formatted_address);
                    setSelectedPlace(place);
                    setPredictions([]);

                    if (place.geometry && place.geometry.location) {
                        const coords = {
                            lat: place.geometry.location.lat(),
                            lng: place.geometry.location.lng()
                        };
                        setCoordinates(coords);
                    }

                    const zipComponent = place.address_components.find(
                        component => component.types.includes('postal_code')
                    );
                    if (zipComponent) {
                        setZipCode(zipComponent.long_name);
                    }

                    const fullAddress = `${place.formatted_address}${unit ? ` Unit ${unit}` : ''}`;
                    setLinkUrl(fullAddress);
                }
            }
        );
    };

    const handleAddressBlur = async () => {
        if (workAddress && !selectedPlace) {
            try {
                const coords = await geocodeAddress(workAddress);
                setCoordinates(coords);
                setLinkUrl(workAddress);
                console.log('Geocoded coordinates for manual address:', coords);
            } catch (error) {
                console.error('Failed to geocode address:', error);
                setLinkUrl(workAddress);
            }
        }
    };

    useEffect(() => {
        if (!open || contactLinkType !== 'Address') {
            setWorkAddress('');
            setZipCode('');
            setUnit('');
            setPredictions([]);
            setSelectedPlace(null);
            setCoordinates(null);

            const input = document.getElementById('work-address-input');
            if (input && input.dataset.autocompleteHandler) {
                input.removeEventListener('input', input.dataset.autocompleteHandler);
                delete input.dataset.autocompleteHandler;
            }
        }
    }, [open, contactLinkType]);

    // ✅ ADD: Load card data when modal opens
    useEffect(() => {
        if (open && cardId && !cardState?.[cardId]) {
            console.log('🔍 ContactLinkDetailModal: Loading card data for cardId:', cardId);

            // Try to load card data if not available in context
            if (loadCardFromServer && !isTemporaryCardFromContext(cardId)) {
                loadCardFromServer(cardId)
                    .then(response => {
                        console.log('🔍 ContactLinkDetailModal: Card data loaded successfully:', response);
                    })
                    .catch(error => {
                        console.error('🔍 ContactLinkDetailModal: Error loading card data:', error);
                    });
            }
        }
    }, [open, cardId, cardState, loadCardFromServer, isTemporaryCardFromContext]);

    // ✅ ADD: Load real card data from server when modal opens (for existing cards)
    useEffect(() => {
        if (open && cardId && !isTemporaryCardFromContext(cardId)) {
            console.log('🔍 ContactLinkDetailModal: Modal opened, attempting to load real card data from server');

            const fetchRealCardData = async () => {
                try {
                    const directResponse = await getCardById(cardId);
                    console.log('🔍 ContactLinkDetailModal: Real card data from server:', directResponse);

                    if (directResponse && directResponse.card && directResponse.card.contact_info) {
                        const hasRealData = directResponse.card.contact_info.fullName ||
                            directResponse.card.contact_info.jobTitle ||
                            directResponse.card.contact_info.company;

                        if (hasRealData) {
                            console.log('🔍 ContactLinkDetailModal: Found real card data, updating context');

                            // Update the card data in the context with real server data
                            const updatedCardData = {
                                ...cardState[cardId],
                                cardFullName: directResponse.card.contact_info.fullName || '',
                                cardJobTitle: directResponse.card.contact_info.jobTitle || '',
                                cardCompany: directResponse.card.contact_info.company || '',
                                cardEmail: directResponse.card.contact_info.email || '',
                                cardPhoneNumber: directResponse.card.contact_info.phone || '',
                                cardLocation: directResponse.card.contact_info.location || '',
                                cardBio: directResponse.card.bio || '',
                                cardProfilePictureUrl: directResponse.card.profilePictureUrl || '',
                                cardCoverPhotoUrl: directResponse.card.coverPhotoUrl || '',
                                cardCompanyLogoUrl: directResponse.card.companyLogoUrl || '',
                                cardTheme: directResponse.card.cardTheme || '',
                                contextCardLayout: directResponse.card.cardLayout || 'center-aligned'
                            };

                            // Update the context
                            if (updateCardData) {
                                updateCardData(cardId, updatedCardData, false); // false = don't sync back to server
                                console.log('🔍 ContactLinkDetailModal: Context updated with real card data:', updatedCardData);
                            }
                        } else {
                            console.log('🔍 ContactLinkDetailModal: No real card data found in server response');
                        }
                    }
                } catch (error) {
                    console.error('🔍 ContactLinkDetailModal: Error fetching real card data:', error);
                }
            };

            // Fetch real card data
            fetchRealCardData();
        }
    }, [open, cardId, isTemporaryCardFromContext]); // ✅ FIXED: Removed cardState and updateCardData from dependencies

    // ✅ ADD: Load card data from server when context data is empty
    useEffect(() => {
        if (open && cardId && cardState?.[cardId]) {
            const card = cardState[cardId];
            const hasEmptyFields = !card.cardFullName && !card.cardJobTitle && !card.cardCompany;

            if (hasEmptyFields) {
                console.log('🔍 ContactLinkDetailModal: Card data exists but fields are empty, attempting to reload from server');

                // ✅ ADD: Direct API call to see what the server actually returns
                const fetchDirectCardData = async () => {
                    try {
                        console.log('🔍 ContactLinkDetailModal: Making direct API call to getCardById for cardId:', cardId);
                        const directResponse = await getCardById(cardId);
                        console.log('🔍 ContactLinkDetailModal: Direct API response:', directResponse);

                        if (directResponse && directResponse.card) {
                            console.log('🔍 ContactLinkDetailModal: Direct API card data:', {
                                hasCard: !!directResponse.card,
                                cardKeys: Object.keys(directResponse.card),
                                hasContactInfo: !!directResponse.card.contact_info,
                                contactInfoKeys: directResponse.card.contact_info ? Object.keys(directResponse.card.contact_info) : [],
                                fullName: directResponse.card.contact_info?.fullName,
                                jobTitle: directResponse.card.contact_info?.jobTitle,
                                company: directResponse.card.contact_info?.company,
                                email: directResponse.card.contact_info?.email,
                                phone: directResponse.card.contact_info?.phone,
                                location: directResponse.card.contact_info?.location,
                                bio: directResponse.card.bio,
                                profilePictureUrl: directResponse.card.profilePictureUrl,
                                coverPhotoUrl: directResponse.card.coverPhotoUrl,
                                companyLogoUrl: directResponse.card.companyLogoUrl,
                                theme: directResponse.card.cardTheme,
                                layout: directResponse.card.cardLayout
                            });

                            // ✅ ADD: Update the card data in context with real server data
                            if (directResponse.card.contact_info?.fullName || directResponse.card.contact_info?.jobTitle || directResponse.card.contact_info?.company) {
                                console.log('🔍 ContactLinkDetailModal: Updating card data in context with real server data');

                                // Update the card data in the context
                                const updatedCardData = {
                                    ...cardState[cardId],
                                    cardFullName: directResponse.card.contact_info?.fullName || '',
                                    cardJobTitle: directResponse.card.contact_info?.jobTitle || '',
                                    cardCompany: directResponse.card.contact_info?.company || '',
                                    cardEmail: directResponse.card.contact_info?.email || '',
                                    cardPhoneNumber: directResponse.card.contact_info?.phone || '',
                                    cardLocation: directResponse.card.contact_info?.location || '',
                                    cardBio: directResponse.card.bio || '',
                                    cardProfilePictureUrl: directResponse.card.profilePictureUrl || '',
                                    cardCoverPhotoUrl: directResponse.card.coverPhotoUrl || '',
                                    cardCompanyLogoUrl: directResponse.card.companyLogoUrl || '',
                                    cardTheme: directResponse.card.cardTheme || '',
                                    contextCardLayout: directResponse.card.cardLayout || 'center-aligned'
                                };

                                // Update the context
                                if (updateCardData) {
                                    updateCardData(cardId, updatedCardData, false); // false = don't sync back to server
                                    console.log('🔍 ContactLinkDetailModal: Card data updated in context:', updatedCardData);
                                }
                            }
                        }
                    } catch (error) {
                        console.error('🔍 ContactLinkDetailModal: Error in direct API call:', error);
                    }
                };

                // Make the direct API call
                fetchDirectCardData();

                // Try to reload card data from server if fields are empty
                if (loadCardFromServer && !isTemporaryCardFromContext(cardId)) {
                    loadCardFromServer(cardId)
                        .then(response => {
                            console.log('🔍 ContactLinkDetailModal: Card data reloaded from server:', response);

                            // ✅ ADD: Debug the server response structure
                            if (response) {
                                console.log('🔍 ContactLinkDetailModal: Server response structure:', {
                                    hasResponse: !!response,
                                    responseType: typeof response,
                                    responseKeys: response ? Object.keys(response) : [],
                                    hasCard: !!response?.card,
                                    cardKeys: response?.card ? Object.keys(response.card) : [],
                                    hasContactInfo: !!response?.card?.contact_info,
                                    contactInfoKeys: response?.card?.contact_info ? Object.keys(response.card.contact_info) : [],
                                    fullName: response?.card?.contact_info?.fullName,
                                    jobTitle: response?.card?.contact_info?.jobTitle,
                                    company: response?.card?.contact_info?.company,
                                    email: response?.card?.contact_info?.email,
                                    phone: response?.card?.contact_info?.phone,
                                    location: response?.card?.contact_info?.location,
                                    bio: response?.card?.bio,
                                    profilePictureUrl: response?.card?.profilePictureUrl,
                                    coverPhotoUrl: response?.card?.coverPhotoUrl,
                                    companyLogoUrl: response?.card?.companyLogoUrl,
                                    theme: response?.card?.cardTheme,
                                    layout: response?.card?.cardLayout
                                });
                            }
                        })
                        .catch(error => {
                            console.error('🔍 ContactLinkDetailModal: Error reloading card data:', error);
                        });
                }
            }
        }
    }, [open, cardId, loadCardFromServer, isTemporaryCardFromContext]); // ✅ FIXED: Removed cardState from dependencies

    // ✅ ADD: Debug logging for card data changes
    useEffect(() => {
        if (open && cardId) {
            console.log('🔍 ContactLinkDetailModal: Card data changed:', {
                cardId,
                cardData: cardState?.[cardId],
                hasCardData: !!cardState?.[cardId],
                cardStateKeys: Object.keys(cardState || {}),
                contactLinksCount: contactLinks?.length || 0
            });
        }
    }, [open, cardId, contactLinks]); // ✅ FIXED: Removed cardState from dependencies

    // ✅ ADD: Log cardData being passed to DigitalCardPreview
    useEffect(() => {
        if (open && cardId && cardState?.[cardId]) {
            const cardDataToPass = {
                cardFullName: cardState[cardId].cardFullName || cardState[cardId].fullName || 'Your Name',
                cardJobTitle: cardState[cardId].cardJobTitle || cardState[cardId].jobTitle || 'Job Title',
                cardCompany: cardState[cardId].cardCompany || cardState[cardId].company || 'Company Name',
                cardProfilePictureUrl: cardState[cardId].cardProfilePictureUrl || cardState[cardId].profilePictureUrl || '',
                cardCoverPhotoUrl: cardState[cardId].cardCoverPhotoUrl || cardState[cardId].coverPhotoUrl || '',
                cardCompanyLogoUrl: cardState[cardId].cardCompanyLogoUrl || cardState[cardId].companyLogoUrl || '',
                cardTheme: cardState[cardId].cardTheme || cardState[cardId].theme || '#FF7A00', // ✅ FIXED: Use actual default orange color from theme
                cardLocation: cardState[cardId].cardLocation || cardState[cardId].location || 'Location',
                cardBio: cardState[cardId].cardBio || cardState[cardId].bio || 'Professional bio and description',
                cardPronoun: cardState[cardId].cardPronoun || cardState[cardId].pronoun || 'he',
                contextCardLayout: cardState[cardId].contextCardLayout || cardState[cardId].cardLayout || 'center-aligned',
                cardLinkColor: cardState[cardId].cardLinkColor || cardState[cardId].linkColor || '#00796B', // ✅ FIXED: Use actual default secondary color from theme
                customLinkTypeLabels: cardState[cardId].customLinkTypeLabels || {
                    'Contact': 'Contact',
                    'Social': 'Social',
                    'Products & Services': 'Products & Services'
                }
            };

            console.log('🔍 ContactLinkDetailModal: cardData being passed to DigitalCardPreview:', cardDataToPass);

            // ✅ ADD: Debug the actual values being used
            console.log('🔍 ContactLinkDetailModal: Raw values from cardState:', {
                cardFullName: cardState[cardId].cardFullName,
                cardJobTitle: cardState[cardId].cardJobTitle,
                cardCompany: cardState[cardId].cardCompany,
                fallbackFullName: cardState[cardId].cardFullName || cardState[cardId].fullName || 'Your Name',
                fallbackJobTitle: cardState[cardId].cardJobTitle || cardState[cardId].jobTitle || 'Job Title',
                fallbackCompany: cardState[cardId].cardCompany || cardState[cardId].company || 'Company Name'
            });
        }
    }, [open, cardId]); // ✅ FIXED: Removed cardState from dependencies

    useEffect(() => {
        if (open && cardId) {
            // Console log for development
            console.log('🔍 ContactLinkDetailModal: Setting current card:', cardId);

            // Send to Sentry only in production
            if (process.env.NODE_ENV === 'production') {
                Sentry.addBreadcrumb({
                    category: 'contact-link-detail-modal',
                    message: 'Setting Current Card',
                    data: { cardId },
                    level: 'info'
                });
            }
            setCurrentCard(cardId);

            // ✅ ADD: Load card data from CardContext to ensure preview works
            if (cardState && cardState[cardId]) {
                console.log('🔍 ContactLinkDetailModal: Card data found in context:', cardState[cardId]);
            } else {
                console.log('🔍 ContactLinkDetailModal: No card data found in context for cardId:', cardId);
                // Try to load card data if not available
                if (loadCardFromServer && !isTemporaryCardFromContext(cardId)) {
                    loadCardFromServer(cardId).catch(error => {
                        console.error('🔍 ContactLinkDetailModal: Error loading card data:', error);
                    });
                }
            }
        }
    }, [open, cardId, setCurrentCard, loadCardFromServer, isTemporaryCardFromContext]); // ✅ FIXED: Removed cardState from dependencies

    useEffect(() => {
        const fetchContactLinks = async () => {
            if (currentFlowType === 'CONTEXT_ONLY') {
                console.log('🔍 ContactLinkDetailModal: CONTEXT_ONLY flow - no backend fetch');
                return;
            }

            if (existingContactLinks) {
                // Contact links already provided
            } else {
                try {
                    if (cardId && currentFlowType === 'DATABASE_SYNC') {
                        const { success, contactLinks } = await getContactLinksByCard(cardId);
                        if (success && contactLinks) {
                            // Contact links fetched
                        }
                    }
                } catch (error) {
                    console.error('Error fetching contact links:', error);
                }
            }
        };

        fetchContactLinks();
    }, [cardId, currentFlowType, existingContactLinks]);

    useEffect(() => {
        if (open && contactLinkType) {
            const baseUrlData = baseUrls.find((baseUrl) => baseUrl.type === contactLinkType);
            if (baseUrlData) {
                setLabel(baseUrlData.label || '');
                setPlaceholder(baseUrlData.placeholder || '');
                setLinkType(baseUrlData.type || '');
                setLinkTitle('');
                setLinkUrl('');
                setWorkAddress('');
                setZipCode('');
                setUnit('');
                setPredictions([]);
                setSelectedPlace(null);
                setCoordinates(null);
                setProductsServicesData({});
                setBaseUrl(baseUrlData.baseUrl);
            }
        }
    }, [open, contactLinkType, baseUrls]);

    const handleLinkTitleChange = (event) => setLinkTitle(event.target.value);
    const handleLinkUrlChange = (event) => {
        if (isPhoneContactType(contactLinkType)) {
            return;
        }
        setLinkUrl(event.target.value);
    };

    const handlePhoneChange = (value) => {
        setLinkUrl(value);
        setPhoneError('');
    };

    // ✅ ENHANCED: Products & Services data change handler with debugging
    const handleProductsServicesDataChange = (data) => {
        console.log('🔍 Products & Services data changed in modal:', data);
        console.log('🔍 Images in received data:', data.images);
        console.log('🔍 Videos in received data:', data.videos);

        // ✅ Enhanced debugging for videos specifically
        if (data.videos && Array.isArray(data.videos)) {
            console.log('🔍 ContactLinkDetailModal: Products & Services videos detected:', {
                videosCount: data.videos.length,
                videos: data.videos,
                contactLinkType
            });
        }

        setProductsServicesData(data);
        if (data.productTitle) {
            setLinkTitle(data.productTitle);
        }
        setLinkUrl('custom');
    };

    const formattedLink = React.useMemo(() => {
        if (contactLinkType === 'Address') {
            if (!workAddress) return '';
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workAddress)}`;
        }

        if (contactLinkType === 'Products & Services') {
            return 'custom';
        }

        if (!linkUrl) return '';

        if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
            return linkUrl;
        }

        return baseUrl ? `${baseUrl}${linkUrl}` : linkUrl;
    }, [contactLinkType, workAddress, linkUrl, baseUrl]);

    const isUrlMissing = React.useMemo(() => {
        if (contactLinkType === 'Address') {
            return !workAddress?.trim();
        }

        if (contactLinkType === 'Products & Services') {
            const productLink = productsServicesData.link || productsServicesData.ctaButton?.link;
            return !productLink?.trim();
        }

        return !linkUrl?.trim();
    }, [contactLinkType, workAddress, linkUrl, productsServicesData]);

    useEffect(() => {
        if (!isUrlMissing && showUrlRequired) {
            setShowUrlRequired(false);
        }
    }, [isUrlMissing, showUrlRequired]);

    useEffect(() => {
        if (isPhoneContactType(contactLinkType) && !isUrlMissing && phoneError) {
            setPhoneError('');
        }
    }, [contactLinkType, isUrlMissing, phoneError]);

    const handleTestLink = () => {
        if (contactLinkType === 'Products & Services') {
            const testUrl = productsServicesData.link || productsServicesData.ctaButton?.link;
            if (testUrl) {
                const finalUrl = testUrl.startsWith('http') ? testUrl : `https://${testUrl}`;
                window.open(finalUrl, '_blank', 'noopener,noreferrer');
            } else {
                alert('Please add a link URL in the Products & Services form to test.');
            }
            return;
        }

        if (formattedLink) {
            console.log('Testing link:', formattedLink);
            window.open(formattedLink, '_blank', 'noopener,noreferrer');
        } else {
            console.error('Formatted link is empty or invalid.');
        }
    };

    const handleAddLink = () => {
        console.log('🔍 ContactLinkDetailModal: handleAddLink called');

        if (isUrlMissing) {
            if (isPhoneContactType(contactLinkType)) {
                setPhoneError('Phone number is required.');
            }
            setShowUrlRequired(true);
            return;
        }

        if (isLinkBeingCreated) {
            console.log('🔍 ContactLinkDetailModal: Link creation is already in progress, ignoring click');
            return;
        }

        // Force data synchronization for Products & Services
        if (contactLinkType === 'Products & Services') {
            console.log('🔍 Products & Services: Ensuring data synchronization before creation');
            console.log('🔍 Current productsServicesData:', productsServicesData);

            // ✅ Enhanced debugging for videos
            if (productsServicesData.videos && Array.isArray(productsServicesData.videos)) {
                console.log('🔍 ContactLinkDetailModal: Videos being processed for creation:', {
                    videosCount: productsServicesData.videos.length,
                    videos: productsServicesData.videos
                });
            }

            // Send to Sentry only in production
            if (process.env.NODE_ENV === 'production') {
                Sentry.addBreadcrumb({
                    category: 'contact-link-detail-modal',
                    message: 'Products & Services Data Before Creation',
                    data: {
                        contactLinkType,
                        cardId,
                        hasImages: !!productsServicesData.images,
                        imagesCount: productsServicesData.images?.length || 0,
                        images: productsServicesData.images,
                        hasVideos: !!productsServicesData.videos,
                        videosCount: productsServicesData.videos?.length || 0,
                        videos: productsServicesData.videos
                    },
                    level: 'info'
                });
            }
        }

        addContactLinkViaRestAPI();
    };

    // ✅ Helper function to update card phone number from user phone number
    const updateCardPhoneNumberIfNeeded = async () => {
        // Only proceed if this is a call/phone contact link type
        if (!isPhoneContactType(contactLinkType)) {
            return;
        }

        try {
            // Get card data to check if it already has a phone number
            const cardResponse = await getCardById(cardId);
            const cardPhoneNumber = cardResponse?.card?.contact_info?.phone;

            // If card already has a phone number, no need to update
            if (cardPhoneNumber) {
                console.log('🔍 Card already has phone number, skipping update');
                return;
            }

            // Get user data to get user's phone number
            if (!userId) {
                console.log('🔍 No userId provided, cannot update card phone number');
                return;
            }

            const userResponse = await getUserById(userId);
            const userPhoneNumber = userResponse?.data?.phone || userResponse?.user?.phone || userResponse?.phone;

            // If user has a phone number, update the card
            if (userPhoneNumber) {
                console.log('🔍 Updating card phone number from user phone number:', userPhoneNumber);

                // Update card with user's phone number
                await updateCard(cardId, {
                    contact_info: {
                        phone: userPhoneNumber
                    }
                });

                // Update card data in context
                if (updateCardData) {
                    updateCardData(cardId, {
                        cardPhoneNumber: userPhoneNumber
                    }, false); // false = don't sync back to server (already synced)
                }

                console.log('✅ Card phone number updated successfully');
            } else {
                console.log('🔍 User does not have a phone number, cannot update card');
            }
        } catch (error) {
            console.error('🔍 Error updating card phone number:', error);
            // Don't throw - this is not critical for contact link creation
        }
    };

    // ✅ CONTEXT-ONLY: Simplified contact link creation with enhanced force refetch
    const addContactLinkViaRestAPI = async () => {
        try {
            // ✅ ADD THIS DEBUG RIGHT AT THE START
            console.log('🔍 MODAL_DEBUG_START:', {
                cardId: cardId?.substring(0, 12) + '...',
                cardIdLength: cardId?.length,
                isUUID: cardId && cardId.includes('-'),
                userId: userId?.substring(0, 10) + '...',
                contactLinkType,
                currentCardId: currentCardId?.substring(0, 12) + '...'
            });

            // ✅ ADDED: Protection against rapid successive calls
            if (isLinkBeingCreated) {
                console.log('🔍 ContactLinkDetailModal: Contact link creation already in progress');
                return;
            }

            setIsLinkBeingCreated(true);
            setSnackbar({ open: false, message: '', severity: 'info' });

            // ✅ FLOW DETECTION: Determine if this is context-only or database sync
            const flowType = determineFlowType();

            // ✅ ADD THIS DEBUG AFTER FLOW DETECTION
            console.log('🔍 MODAL_FLOW_DEBUG:', {
                flowType,
                shouldBeContextOnly: flowType === 'CONTEXT_ONLY'
            });

            // ✅ IMAGE UPLOAD: Upload custom icon if it's a new cropped blob
            let finalIconUrl = croppedIcon;
            if (croppedIconBlob) {
                console.log('🔍 ContactLinkDetailModal: Uploading cropped icon blob...');
                setUploadingIcon(true);
                try {
                    const uploadedUrl = await uploadImageBlob(
                        croppedIconBlob,
                        null,
                        'contact-link',
                        'icon',
                        cardId || Date.now().toString()
                    );
                    console.log('✅ ContactLinkDetailModal: Icon uploaded successfully:', uploadedUrl);
                    finalIconUrl = uploadedUrl;
                    setCroppedIcon(uploadedUrl);
                    setCroppedIconBlob(null); // Clear blob after upload
                } catch (uploadError) {
                    console.error('❌ ContactLinkDetailModal: Icon upload failed:', uploadError);
                    // Prevent falling back to a blob: or base64 URL which will fail on backend
                    finalIconUrl = ''; 
                    throw new Error(`Failed to upload icon: ${uploadError.message}. Please check your internet connection or try a different image.`);
                } finally {
                    setUploadingIcon(false);
                }
            }

            const debugInfo = { flowType, cardId, userId };

            // Console log for development
            console.log('🔍 CONTEXT-ONLY: Flow determination:', debugInfo);

            // Send to Sentry only in production
            if (process.env.NODE_ENV === 'production') {
                Sentry.addBreadcrumb({
                    category: 'contact-link-detail-modal',
                    message: 'Context Only Flow Determination',
                    data: debugInfo,
                    level: 'info'
                });
            }

            // ✅ CONTEXT-ONLY FLOWS: Signup or Card Creation
            if (flowType === 'CONTEXT_ONLY') {
                // ✅ Update card phone number if needed (for call links)
                // Note: For context-only flows, we'll update when the card is saved
                // But we can still update the context state
                if (isPhoneContactType(contactLinkType) && userId) {
                    try {
                        const userResponse = await getUserById(userId);
                        const userPhoneNumber = userResponse?.data?.phone || userResponse?.user?.phone || userResponse?.phone;
                        const cardPhoneNumber = cardState[cardId]?.cardPhoneNumber || cardState[cardId]?.phone;

                        // If user has phone but card doesn't, update context
                        if (userPhoneNumber && !cardPhoneNumber && updateCardData) {
                            console.log('🔍 CONTEXT-ONLY: Updating card phone number in context:', userPhoneNumber);
                            updateCardData(cardId, {
                                cardPhoneNumber: userPhoneNumber
                            }, false);
                        }
                    } catch (error) {
                        console.error('🔍 CONTEXT-ONLY: Error updating card phone number:', error);
                    }
                }

                // Console log for development
                console.log('🔍 CONTEXT-ONLY: Creating contact link in context only');

                // Send to Sentry only in production
                if (process.env.NODE_ENV === 'production') {
                    Sentry.addBreadcrumb({
                        category: 'contact-link-detail-modal',
                        message: 'Creating Contact Link in Context Only',
                        data: { contactLinkType, cardId },
                        level: 'info'
                    });
                }

                // Create contact link data for context
                const contactLinkData = {
                    type: contactLinkType,
                    details: {
                        ...(previewContactLink?.details || {}),
                        icon: finalIconUrl // Use the uploaded URL
                    },
                    isHighlighted: false,
                    cardId: cardId
                };

                console.log('🔍 CONTEXT_ONLY: Contact link data being created:', contactLinkData);
                console.log('🔍 CONTEXT_ONLY: Images in contact link data:', contactLinkData.details.images);
                console.log('🔍 CONTEXT_ONLY: Videos in contact link data:', contactLinkData.details.videos);

                console.log('🔍 CONTACT_LINK_TYPE_DEBUG:', {
                    contactLinkType,
                    expectedCategory: getContactLinkCategory(contactLinkType),
                    contactLinkData
                });

                // 🔍 DEBUG THE FINAL PAYLOAD
                debugContactLinkCreation(contactLinkData);

                // ✅ CONTEXT-ONLY: Add directly to context, no API call
                const tempLink = {
                    ...contactLinkData,
                    _id: `temp_${Date.now()}_${Math.random()}`,
                    id: `temp_${Date.now()}_${Math.random()}`,
                    cardId: cardId,
                    tempCardId: cardId // Mark as temporary
                };

                // ✅ ADD THIS DEBUG BEFORE ADDING TO CONTEXT
                console.log('🔍 ADDING_TO_CONTEXT:', {
                    linkId: tempLink._id,
                    cardId: tempLink.cardId,
                    type: tempLink.type,
                    isVisible: tempLink.isVisible
                });

                // Console log for development
                console.log('🔍 CONTEXT-ONLY: Adding to context:', tempLink);

                // Send to Sentry only in production
                if (process.env.NODE_ENV === 'production') {
                    Sentry.addBreadcrumb({
                        category: 'contact-link-detail-modal',
                        message: 'Adding Contact Link to Context',
                        data: { tempLink, cardId },
                        level: 'info'
                    });
                }
                addContactLink(tempLink);

                // ✅ ADD THIS DEBUG AFTER ADDING TO CONTEXT
                setTimeout(() => {
                    console.log('🔍 CONTEXT_AFTER_ADD: Link should be added now');
                }, 200);

                setSuccessSnackbarOpen(true);
                setContactLink(tempLink);
                setTimeout(() => handleModalClose(), 500);
                return;
            }

            // ✅ DATABASE_SYNC FLOWS: CRUD operations on existing cards
            // Console log for development
            console.log('🔍 DATABASE_SYNC: Creating contact link via API');

            // Send to Sentry only in production
            if (process.env.NODE_ENV === 'production') {
                Sentry.addBreadcrumb({
                    category: 'contact-link-detail-modal',
                    message: 'Creating Contact Link via API',
                    data: { contactLinkType, cardId, userId },
                    level: 'info'
                });
            }

            const contactLinkData = {
                type: contactLinkType,
                details: {
                    ...(previewContactLink?.details || {}),
                    icon: finalIconUrl // Use the uploaded URL
                },
                isHighlighted: false,
                userId: userId,
                cardId: cardId
            };

            console.log('🔍 DATABASE_SYNC: Contact link data being created:', contactLinkData);
            console.log('🔍 DATABASE_SYNC: Images in contact link data:', contactLinkData.details.images);
            console.log('🔍 DATABASE_SYNC: Videos in contact link data:', contactLinkData.details.videos);

            // 🔍 DEBUG THE FINAL PAYLOAD
            debugContactLinkCreation(contactLinkData);

            const response = await createContactLink(contactLinkData);
            console.log('🔍 DATABASE_SYNC: Response:', response);

            if (response.success) {
                console.log('🔍 DATABASE_SYNC: Contact link created successfully');
                setSuccessSnackbarOpen(true);

                const linkToAdd = {
                    ...response.contactLink,
                    cardId: cardId
                };

                addContactLink(linkToAdd);
                setContactLink(response.contactLink);

                // ✅ PHONE NUMBER SYNC: If this is a call link and card doesn't have phone number, update from user
                if (isPhoneContactType(contactLinkType) && userId && cardId) {
                    try {
                        // Get card to check if it has phone number
                        const cardResponse = await getCardById(cardId);
                        const cardPhoneNumber = cardResponse?.card?.contact_info?.phone;

                        // If card doesn't have phone number, get from user and update card
                        if (!cardPhoneNumber) {
                            const userResponse = await getUserById(userId);
                            const userPhoneNumber = userResponse?.data?.phone || userResponse?.user?.phone || userResponse?.phone;

                            if (userPhoneNumber) {
                                console.log('🔍 Updating card phone number from user phone number:', userPhoneNumber);

                                // Update card with user's phone number
                                await updateCard(cardId, {
                                    contact_info: {
                                        phone: userPhoneNumber
                                    }
                                });

                                // Update card data in context
                                if (updateCardData) {
                                    updateCardData(cardId, {
                                        cardPhoneNumber: userPhoneNumber
                                    }, false); // false = don't sync back to server (already synced)
                                }

                                console.log('✅ Card phone number updated successfully');
                            }
                        }
                    } catch (error) {
                        console.error('🔍 Error updating card phone number:', error);
                        // Don't throw - this is not critical for contact link creation
                    }
                }

                // ✅ CRITICAL FIX: Force refetch after successful creation for database sync
                const targetCardId = currentCardId || cardId;
                if (targetCardId) {
                    console.log('🔍 ContactLinkDetailModal: Force refetching card after successful creation:', targetCardId);

                    // ✅ SIMPLIFIED: Reduced delay since rate limiting is not needed
                    setTimeout(() => {
                        try {
                            forceRefetchCard(targetCardId);
                        } catch (refetchError) {
                            console.error('🔍 ContactLinkDetailModal: Force refetch failed:', refetchError);
                            // Don't throw - this is not critical for the user experience
                        }
                    }, 500); // Reduced to 500ms since rate limiting is not needed
                }

                setTimeout(() => handleModalClose(), 500);
            } else {
                throw new Error(response.message || 'Failed to create contact link');
            }
        } catch (error) {
            console.error('🔍 CONTACT_LINK_CREATION_ERROR:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to create contact link. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsLinkBeingCreated(false);
        }
    };

    // ✅ ENHANCED: Preview contact link with enhanced video debugging
    const previewContactLink = React.useMemo(() => {
        if (!open) return null;

        if (contactLinkType === 'Address' && workAddress) {
            return {
                type: contactLinkType,
                details: {
                    linkTitle,
                    linkUrl: workAddress,
                    formattedLink: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workAddress)}`,
                    coordinates,
                    icon: croppedIcon
                }
            };
        }

        if (contactLinkType === 'Products & Services' && productsServicesData.productTitle) {
            const previewData = {
                type: contactLinkType,
                details: {
                    // Core fields
                    linkTitle: productsServicesData.productTitle,
                    linkUrl: 'custom',
                    formattedLink: 'custom',
                    icon: croppedIcon,

                    // Products & Services specific fields
                    productTitle: productsServicesData.productTitle,
                    description: productsServicesData.description || '',
                    link: productsServicesData.link || '',
                    ctaText: productsServicesData.ctaText || 'Learn More',

                    // Media arrays - ensure they're always arrays
                    images: Array.isArray(productsServicesData.images) ?
                        productsServicesData.images :
                        (productsServicesData.images ? [productsServicesData.images] : []),
                    videos: Array.isArray(productsServicesData.videos) ?
                        productsServicesData.videos :
                        (productsServicesData.videos ? [productsServicesData.videos] : []),

                    // CTA button
                    ctaButton: {
                        text: productsServicesData.ctaText || 'Learn More',
                        link: productsServicesData.link || ''
                    },

                    // Additional details for backward compatibility
                    additionalDetails: {
                        productTitle: productsServicesData.productTitle,
                        description: productsServicesData.description || '',
                        link: productsServicesData.link || '',
                        ctaText: productsServicesData.ctaText || 'Learn More',
                        images: Array.isArray(productsServicesData.images) ?
                            productsServicesData.images :
                            (productsServicesData.images ? [productsServicesData.images] : []),
                        videos: Array.isArray(productsServicesData.videos) ?
                            productsServicesData.videos :
                            (productsServicesData.videos ? [productsServicesData.videos] : [])
                    }
                }
            };

            console.log('🔍 PREVIEW_CONTACT_LINK: Products & Services preview created:', previewData);
            console.log('🔍 PREVIEW_CONTACT_LINK: Images in preview:', previewData.details.images);
            console.log('🔍 PREVIEW_CONTACT_LINK: Videos in preview:', previewData.details.videos);
            console.log('🔍 PREVIEW_CONTACT_LINK: Images in additionalDetails:', previewData.details.additionalDetails.images);
            console.log('🔍 PREVIEW_CONTACT_LINK: Videos in additionalDetails:', previewData.details.additionalDetails.videos);

            return previewData;
        }

        if (contactLinkType !== 'Address' && contactLinkType !== 'Products & Services' && linkUrl) {
            return {
                type: contactLinkType,
                details: {
                    linkTitle,
                    linkUrl,
                    formattedLink,
                    icon: croppedIcon
                }
            };
        }

        return null;
    }, [open, contactLinkType, workAddress, linkTitle, linkUrl, formattedLink, croppedIcon, productsServicesData, coordinates]);

    return (
        <>
            <TeamModal
                open={open}
                onClose={handleModalClose} // ✅ Use enhanced close handler
                aria-labelledby="contact-links-detail-modal"
                width="55%"
                maxWidth={1000}
                height="85%"
                top="5%"
                borderRadius={3}
                overflow="hidden"
            >
                <Box
                    sx={{
                        display: 'flex',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    {/* Left Side */}
                    <Box
                        sx={{
                            width: '60.67%',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                            padding: '10px',
                        }}
                    >
                        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                            {React.cloneElement(getIconForType(contactLinkType), {
                                sx: {
                                    mr: 2,
                                    fontSize: '24px',
                                    color: '#666',
                                    padding: '4px',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '4px',
                                }
                            })}
                            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f' }}>
                                {contactLinkType}
                            </Typography>
                            {contactLinkType !== 'Products & Services' && (
                                <Button
                                    onClick={handleIconPickerOpen}
                                    sx={{
                                        ml: 2,
                                        fontSize: '0.8rem',
                                        color: '#2196f3',
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(33, 150, 243, 0.04)'
                                        }
                                    }}
                                >
                                    Select photo here or drag and drop
                                </Button>
                            )}
                            <input
                                ref={iconInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleIconUpload}
                            />
                        </Box>

                        <TeamAssetPickerPopover
                            anchorEl={iconPickerAnchorEl}
                            onClose={handleIconPickerClose}
                            onUploadClick={handleUploadOwnIcon}
                            assets={linkIconAssets}
                            onSelectAsset={handleSelectTeamAssetIcon}
                            uploadButtonText="Upload your own link icon"
                            previewWidth="70px"
                            previewHeight="70px"
                            previewRadius="8px"
                            onEmptyActionClick={() => navigate('/team/team-assets')}
                            paperSx={{ width: '360px', maxWidth: '75vw' }}
                        />

                        <Modal
                            open={isIconCropperOpen}
                            onClose={() => setIsIconCropperOpen(false)}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backdropFilter: 'blur(7px)',
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                        >
                            <Box sx={{
                                outline: 'none',
                                width: '90vw',
                                maxWidth: '900px',
                                height: '90vh',
                                maxHeight: '700px',
                            }}>
                                {iconImage && (
                                    <>
                                        <Box sx={{
                                            position: 'relative',
                                            width: '100%',
                                            height: 'calc(100% - 100px)',
                                            marginBottom: 2,
                                        }}>
                                            <Cropper
                                                image={iconImage}
                                                crop={crop}
                                                zoom={zoom}
                                                aspect={1}
                                                onCropChange={setCrop}
                                                onZoomChange={setZoom}
                                                onCropComplete={onIconCropComplete}
                                                onMediaLoaded={onMediaLoaded}
                                                objectFit="contain"
                                                centerImage={true}
                                                showGrid={false}
                                                minZoom={0.1}
                                                maxZoom={3}
                                                restrictPosition={false}
                                            />
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Slider
                                                value={zoom}
                                                onChange={(e, newValue) => setZoom(newValue)}
                                                min={0.1}
                                                max={3}
                                                step={0.01}
                                                valueLabelDisplay="auto"
                                                defaultValue={1}
                                                sx={{
                                                    marginBottom: 2,
                                                    width: '80%',
                                                    margin: '0 auto 20px auto'
                                                }}
                                            />
                                            <Box>
                                                <Button onClick={() => setIsIconCropperOpen(false)} variant="contained" sx={{ marginRight: 2 }}>
                                                    Cancel
                                                </Button>
                                                <Button onClick={handleIconCropSave} variant="contained" color="primary">
                                                    Crop
                                                </Button>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Modal>

                        {/* Form Content */}
                        {contactLinkType === 'Address' ? (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Work Address
                                </Typography>

                                <TextField
                                    id="work-address-input"
                                    fullWidth
                                    autoFocus
                                    variant="outlined"
                                    size="small"
                                    value={workAddress}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setWorkAddress(newValue);
                                        if (!newValue) {
                                            setPredictions([]);
                                            setSelectedPlace(null);
                                            setZipCode('');
                                            setLinkUrl('');
                                            setCoordinates(null);
                                        }
                                    }}
                                    onBlur={(e) => {
                                        handleAddressBlur(e);
                                        if (!e.target.value?.trim()) {
                                            setShowUrlRequired(true);
                                        }
                                    }}
                                    error={showUrlRequired && isUrlMissing}
                                    helperText={showUrlRequired && isUrlMissing ? 'Address is required.' : ''}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Backspace' && workAddress.length === 1) {
                                            setWorkAddress('');
                                            setPredictions([]);
                                            setSelectedPlace(null);
                                            setZipCode('');
                                            setLinkUrl('');
                                            setCoordinates(null);
                                        }
                                    }}
                                    sx={{ mb: 1 }}
                                />

                                {predictions.length > 0 && (
                                    <Box
                                        sx={{
                                            mt: -1,
                                            mb: 2,
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            bgcolor: 'background.paper',
                                            borderRadius: 1,
                                            boxShadow: 1,
                                        }}
                                    >
                                        {predictions.map((prediction) => (
                                            <Box
                                                key={prediction.place_id}
                                                sx={{
                                                    p: 1,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                    },
                                                }}
                                                onClick={() => handlePlaceSelect(prediction)}
                                            >
                                                <Typography variant="body2">
                                                    {prediction.description}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            ZIP
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={zipCode}
                                            onChange={(e) => setZipCode(e.target.value)}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            UNIT
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={unit}
                                            onChange={(e) => setUnit(e.target.value)}
                                        />
                                    </Box>
                                </Box>

                                {workAddress && (
                                    <Box sx={{ mb: 2, height: 200, border: '1px solid #ddd', borderRadius: 1 }}>
                                        {coordinates ? (
                                            <iframe
                                                title="location-map"
                                                width="100%"
                                                height="100%"
                                                frameBorder="0"
                                                src={`https://www.google.com/maps/embed/v1/view?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&center=${coordinates.lat},${coordinates.lng}&zoom=15`}
                                                allowFullScreen
                                            />
                                        ) : (
                                            <iframe
                                                title="location-map"
                                                width="100%"
                                                height="100%"
                                                frameBorder="0"
                                                src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(workAddress)}`}
                                                allowFullScreen
                                            />
                                        )}
                                    </Box>
                                )}

                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Link Title
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    placeholder="e.g., Office, Home, Shop"
                                    value={linkTitle}
                                    onChange={handleLinkTitleChange}
                                    sx={{ mb: 2 }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleTestLink}
                                    sx={{ mb: 2 }}
                                >
                                    Test Your Link
                                </Button>
                            </Box>
                        ) : contactLinkType === 'Products & Services' ? (
                            <Box sx={{ mb: 2 }}>
                                {/* ✅ ENHANCED: ProductsServicesForm with enhanced debugging */}
                                <TeamProductsServicesForm
                                    onDataChange={handleProductsServicesDataChange}
                                    initialData={productsServicesData}
                                    cardId={cardId}
                                    userId={userId}
                                />
                                {showUrlRequired && isUrlMissing && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        Link URL is required.
                                    </Typography>
                                )}

                            </Box>
                        ) : (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {label || 'Label'}
                                </Typography>
                                {isPhoneContactType(contactLinkType) ? (
                                    <TeamPhoneInputField
                                        value={linkUrl}
                                        onChange={handlePhoneChange}
                                        error={phoneError}
                                        country="sg"
                                        placeholder={placeholder || 'Enter phone number'}
                                        containerStyle={{ marginBottom: '16px' }}
                                    />
                                ) : (
                                    <TextField
                                        fullWidth
                                        autoFocus
                                        variant="outlined"
                                        size="small"
                                        placeholder={placeholder || 'Text or String'}
                                        value={linkUrl}
                                        onChange={handleLinkUrlChange}
                                        onBlur={(e) => {
                                            if (!e.target.value?.trim()) {
                                                setShowUrlRequired(true);
                                            }
                                        }}
                                        error={showUrlRequired && isUrlMissing}
                                        helperText={showUrlRequired && isUrlMissing ? 'Link URL is required.' : ''}
                                        sx={{ mb: 2 }}
                                    />
                                )}
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Link Title
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    placeholder={contactLinkType}
                                    value={linkTitle}
                                    onChange={handleLinkTitleChange}
                                    sx={{ mb: 2 }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleTestLink}
                                    sx={{ mb: 2 }}
                                >
                                    Test Your Link
                                </Button>
                            </Box>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>
                            <Button variant="outlined" onClick={handleModalClose} sx={{ mr: 1 }} disabled={isLinkBeingCreated}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddLink}
                                disabled={isLinkBeingCreated || isUrlMissing}
                                startIcon={isLinkBeingCreated ? <CircularProgress size={16} color="inherit" /> : null}
                                sx={{
                                    minWidth: 120,
                                    pointerEvents: isLinkBeingCreated || isUrlMissing ? 'none' : 'auto'
                                }}
                            >
                                {isLinkBeingCreated ? 'Adding Link...' : 'Add Link'}
                            </Button>
                        </Box>
                    </Box>

                    {/* Right Side */}
                    <Box
                        sx={{
                            width: '39.33%',
                            borderLeft: '1px solid #ddd',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton onClick={handleModalClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box sx={{ flex: 1, pl: 2, pr: 2 }}>
                            {/* ✅ ADD: Debug logging for card data - COMMENTED OUT FOR PRODUCTION */}
                            {/* 
              {process.env.NODE_ENV === 'development' && (
                <Box sx={{ mb: 2, p: 1, backgroundColor: '#f0f0f0', borderRadius: 1, fontSize: '0.75rem' }}>
                  <div>🔍 Debug: cardId = {cardId}</div>
                  <div>🔍 Debug: cardState[cardId] = {cardState?.[cardId] ? 'Available' : 'Not Available'}</div>
                  <div>🔍 Debug: contactLinks count = {contactLinks?.length || 0}</div>
                  <div>🔍 Debug: previewContactLink = {previewContactLink ? 'Available' : 'Not Available'}</div>
                  <div>🔍 Debug: contactLinks types = {contactLinks?.map(l => l.type).join(', ') || 'None'}</div>
                  <div>🔍 Debug: cardState keys = {Object.keys(cardState || {}).join(', ') || 'None'}</div>

                  {cardState?.[cardId] && (
                    <>
                      <div>🔍 Debug: cardState[cardId] keys = {Object.keys(cardState[cardId]).join(', ')}</div>
                      <div>🔍 Debug: cardFullName = {cardState[cardId].cardFullName || 'NULL'}</div>
                      <div>🔍 Debug: cardJobTitle = {cardState[cardId].cardJobTitle || 'NULL'}</div>
                      <div>🔍 Debug: cardCompany = {cardState[cardId].cardCompany || 'NULL'}</div>
                      <div>🔍 Debug: cardProfilePictureUrl = {cardState[cardId].cardProfilePictureUrl ? 'Has URL' : 'No URL'}</div>
                      <div>🔍 Debug: cardCoverPhotoUrl = {cardState[cardId].cardCoverPhotoUrl ? 'Has URL' : 'No URL'}</div>
                      <div>🔍 Debug: cardCompanyLogoUrl = {cardState[cardId].cardCompanyLogoUrl ? 'Has URL' : 'No URL'}</div>
                      <div>🔍 Debug: cardTheme = {cardState[cardId].cardTheme || 'NULL'}</div>
                      <div>🔍 Debug: cardLocation = {cardState[cardId].cardLocation || 'NULL'}</div>
                      <div>🔍 Debug: cardBio = {cardState[cardId].cardBio || 'NULL'}</div>
                      <div>🔍 Debug: cardPronoun = {cardState[cardId].cardPronoun || 'NULL'}</div>
                      <div>🔍 Debug: contextCardLayout = {cardState[cardId].contextCardLayout || 'NULL'}</div>
                      <div>🔍 Debug: cardLinkColor = {cardState[cardId].cardLinkColor || 'NULL'}</div>

                      <div>🔍 Debug: fullName = {cardState[cardId].fullName || 'NULL'}</div>
                      <div>🔍 Debug: jobTitle = {cardState[cardId].jobTitle || 'NULL'}</div>
                      <div>🔍 Debug: company = {cardState[cardId].company || 'NULL'}</div>
                      <div>🔍 Debug: profilePictureUrl = {cardState[cardId].profilePictureUrl ? 'Has URL' : 'No URL'}</div>
                      <div>🔍 Debug: coverPhotoUrl = {cardState[cardId].coverPhotoUrl ? 'Has URL' : 'No URL'}</div>
                      <div>🔍 Debug: companyLogoUrl = {cardState[cardId].companyLogoUrl ? 'Has URL' : 'No URL'}</div>
                      <div>🔍 Debug: theme = {cardState[cardId].theme || 'NULL'}</div>
                      <div>🔍 Debug: location = {cardState[cardId].location || 'NULL'}</div>
                      <div>🔍 Debug: bio = {cardState[cardId].bio || 'NULL'}</div>
                      <div>🔍 Debug: pronoun = {cardState[cardId].pronoun || 'NULL'}</div>
                      <div>🔍 Debug: cardLayout = {cardState[cardId].cardLayout || 'NULL'}</div>
                      <div>🔍 Debug: linkColor = {cardState[cardId].linkColor || 'NULL'}</div>

                      <div>🔍 Debug: cardName = {cardState[cardId].cardName || 'NULL'}</div>
                      <div>🔍 Debug: name = {cardState[cardId].name || 'NULL'}</div>
                      <div>🔍 Debug: title = {cardState[cardId].title || 'NULL'}</div>
                      <div>🔍 Debug: organization = {cardState[cardId].organization || 'NULL'}</div>
                      <div>🔍 Debug: employer = {cardState[cardId].employer || 'NULL'}</div>

                      <div>🔍 Debug: Final cardFullName = {cardState[cardId].cardFullName || cardState[cardId].fullName || cardState[cardId].cardName || cardState[cardId].name || 'Your Name'}</div>
                      <div>🔍 Debug: Final cardJobTitle = {cardState[cardId].cardJobTitle || cardState[cardId].jobTitle || cardState[cardId].title || 'Job Title'}</div>
                      <div>🔍 Debug: Final cardCompany = {cardState[cardId].cardCompany || cardState[cardId].company || cardState[cardId].organization || cardState[cardId].employer || 'Company Name'}</div>

                      <div>🔍 Debug: Raw card data = {JSON.stringify(cardState[cardId], null, 2).substring(0, 500)}...</div>
                    </>
                  )}
                </Box>
              )}
              */}

                            <TeamMemberDigitalCardPreview
                                cardId={cardId}
                                contactLinks={contactLinks || []}
                                contactLink={previewContactLink}
                                disableLeadCapture={true}
                                // ✅ ADD: Pass card data directly to ensure preview works
                                cardData={{
                                    // Try both prefixed and non-prefixed field names
                                    cardFullName: cardState?.[cardId]?.cardFullName || cardState?.[cardId]?.fullName || cardState?.[cardId]?.cardName || cardState?.[cardId]?.name || 'Your Name',
                                    cardJobTitle: cardState?.[cardId]?.cardJobTitle || cardState?.[cardId]?.jobTitle || cardState?.[cardId]?.title || 'Job Title',
                                    cardCompany: cardState?.[cardId]?.cardCompany || cardState?.[cardId]?.company || cardState?.[cardId]?.organization || cardState?.[cardId]?.employer || 'Company Name',
                                    cardProfilePictureUrl: cardState?.[cardId]?.cardProfilePictureUrl || cardState?.[cardId]?.profilePictureUrl || '',
                                    cardCoverPhotoUrl: cardState?.[cardId]?.cardCoverPhotoUrl || cardState?.[cardId]?.coverPhotoUrl || '',
                                    cardCompanyLogoUrl: cardState?.[cardId]?.cardCompanyLogoUrl || cardState?.[cardId]?.companyLogoUrl || '',
                                    cardTheme: cardState?.[cardId]?.cardTheme || cardState?.[cardId]?.theme || '#FF7A00', // ✅ FIXED: Use actual default orange color from theme
                                    cardLocation: cardState?.[cardId]?.cardLocation || cardState?.[cardId]?.location || 'Location',
                                    cardBio: cardState?.[cardId]?.cardBio || cardState?.[cardId]?.bio || 'Professional bio and description',
                                    cardPronoun: cardState?.[cardId]?.cardPronoun || cardState?.[cardId]?.pronoun || 'he',
                                    contextCardLayout: cardState?.[cardId]?.contextCardLayout || cardState?.[cardId]?.cardLayout || 'center-aligned',
                                    cardLinkColor: cardState?.[cardId]?.cardLinkColor || cardState?.[cardId]?.linkColor || '#00796B', // ✅ FIXED: Use actual default secondary color from theme
                                    customLinkTypeLabels: cardState?.[cardId]?.customLinkTypeLabels || {
                                        'Contact': 'Contact',
                                        'Social': 'Social',
                                        'Products & Services': 'Products & Services'
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </TeamModal>

            <TeamNotificationSnackbar
                open={successSnackbarOpen}
                message={currentFlowType === 'CONTEXT_ONLY'
                    ? `${contactLinkType} successfully added to context`
                    : `${contactLinkType} successfully added to card`}
                severity='success'
                handleClose={handleCloseSuccessSnackbar}
                sx={{ zIndex: 1301 }}
            />

            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleSnackbarClose}
                sx={{ zIndex: 1301 }}
            />
        </>
    );
};

export default TeamContactLinkDetailModal;